// src/pages/admin/AdminMasterDashboard.js
import { useMemo, useState } from "react";
import { getAuth, signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";

import PortalControlsPane from "./PortalControlsPane";
import MembersPane from "./MembersPane";
import DealersPane from "./DealersPane";
import ComponentsPane from "./ComponentsPane";
import ReportsPane from "./ReportsPane";

const cx = (...xs) => xs.filter(Boolean).join(" ");

function AdminMasterDashboard() {
  const [menu, setMenu] = useState("portal"); // portal | members | dealers | components | reports

  const auth = getAuth();
  const navigate = useNavigate();

  /* ✅ LOGOUT HANDLER */
  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/admin-login");
    } catch (err) {
      console.error("Logout failed:", err);
      alert("Logout failed. Please try again.");
    }
  };

  const MENU = useMemo(
    () => [
      {
        id: "portal",
        index: "01",
        icon: "🛡️",
        label: "Portal Controls",
        sub: "Open / Close portals",
      },
      {
        id: "members",
        index: "02",
        icon: "👥",
        label: "Members Management",
        sub: "Approve / Reject / View",
      },
      {
        id: "dealers",
        index: "03",
        icon: "🏷️",
        label: "Dealers / Vendors",
        sub: "Agro & Tractor partners",
      },
      {
        id: "components",
        index: "04",
        icon: "🌾",
        label: "Farmer Components",
        sub: "Planting / Drip / Precision",
      },
      {
        id: "reports",
        index: "05",
        icon: "📑",
        label: "Summary & Reports",
        sub: "Key statistics",
      },
    ],
    []
  );

  const activeMeta = MENU.find((m) => m.id === menu) || MENU[0];

  return (
    <div className="min-h-screen bg-slate-100">
      {/* ================= TOP RIBBON ================= */}
      <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-emerald-950 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] font-semibold opacity-80">
                Shine Agri Allied &amp; Marketing • Central Control Room
              </div>
              <h1 className="text-xl md:text-2xl font-extrabold tracking-tight mt-1">
                Society Admin Master Dashboard
              </h1>
              <div className="text-[11px] opacity-80 mt-1">
                Government-style admin panel • Approvals • Vendors • Farmer Benefit Components • Reports
              </div>
            </div>

            {/* ✅ STATUS + LOGOUT */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] px-3 py-1 rounded-full bg-white/10 border border-white/20">
                🔒 Internal Use Only
              </span>
              <span className="text-[10px] px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-200/30">
                Live System
              </span>

              {/* ✅ LOGOUT BUTTON */}
              <button
                onClick={handleLogout}
                className="text-[11px] px-4 py-1.5 rounded-full bg-rose-600 hover:bg-rose-700 text-white font-semibold shadow-sm"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ================= PAGE SHELL ================= */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* INFO STRIP */}
        <div className="mb-5 grid grid-cols-1 md:grid-cols-3 gap-3">
          <InfoCard
            title="Active Section"
            value={`${activeMeta.icon} ${activeMeta.label}`}
            note={activeMeta.sub}
          />
          <InfoCard
            title="Compliance Note"
            value="Real-time Changes"
            note="Any approval/rejection affects portal data immediately."
          />
          <InfoCard
            title="System Mode"
            value="Premium Admin Panel"
            note="Designed for government / cooperative governance workflows."
          />
        </div>

        <div className="bg-white rounded-3xl shadow-xl border border-slate-200">
          <div className="flex flex-col md:flex-row min-h-[680px] min-w-0">
            {/* ================= SIDEBAR ================= */}
            <aside className="md:w-80 bg-gradient-to-b from-slate-950 via-slate-900 to-emerald-950 text-white p-4 md:p-5">
              <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
                <div className="text-[11px] uppercase tracking-[0.22em] font-semibold opacity-80">
                  Control Sections
                </div>
                <div className="text-xs mt-1 opacity-80">
                  Navigate modules in a single governance dashboard.
                </div>
              </div>

              <nav className="mt-4 space-y-2">
                {MENU.map((item) => (
                  <SideItem
                    key={item.id}
                    active={menu === item.id}
                    onClick={() => setMenu(item.id)}
                    index={item.index}
                    icon={item.icon}
                    label={item.label}
                    sub={item.sub}
                  />
                ))}
              </nav>

              <div className="mt-5 rounded-2xl bg-black/25 border border-white/10 p-4">
                <div className="text-[11px] font-semibold">Guideline</div>
                <ul className="mt-2 text-[11px] opacity-80 space-y-1 list-disc list-inside">
                  <li>Portal Controls → enable/disable modules</li>
                  <li>Members → approve/reject carefully</li>
                  <li>Vendors → verify profile + products</li>
                  <li>Reports → download insights & summary</li>
                </ul>
              </div>
            </aside>

            {/* ================= MAIN ================= */}
            <main className="flex-1 min-w-0 bg-slate-50">
              <div className="px-5 md:px-7 py-5 border-b border-slate-200 bg-white">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                      Current Module
                    </div>
                    <div className="mt-1 text-lg font-extrabold text-slate-900 flex items-center gap-2">
                      <span className="text-xl">{activeMeta.icon}</span>
                      {activeMeta.label}
                      <span className="text-[10px] px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-700">
                        #{activeMeta.index}
                      </span>
                    </div>
                    <div className="text-[11px] text-slate-600 mt-1">
                      {activeMeta.sub}
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-5 md:p-7 min-w-0">
                <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
                  <div className="p-4 md:p-5 min-w-0 overflow-x-auto">
                    {menu === "portal" && <PortalControlsPane />}
                    {menu === "members" && <MembersPane />}
                    {menu === "dealers" && <DealersPane />}
                    {menu === "components" && <ComponentsPane />}
                    {menu === "reports" && <ReportsPane />}
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>

        <div className="mt-4 text-[11px] text-slate-500">
          ⚠️ This dashboard is designed for official administration. Please ensure approvals and actions are performed as per society policy.
        </div>
      </div>
    </div>
  );
}

/* ================= UI COMPONENTS ================= */

function SideItem({ active, onClick, index, icon, label, sub }) {
  return (
    <button
      onClick={onClick}
      className={cx(
        "w-full text-left rounded-2xl border transition-all duration-150 overflow-hidden",
        active
          ? "border-emerald-200/40 bg-emerald-500/20"
          : "border-white/10 bg-white/5 hover:bg-white/10"
      )}
    >
      <div className="flex items-stretch">
        <div className={cx("w-1.5", active ? "bg-emerald-400" : "bg-transparent")} />
        <div className="flex-1 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-lg">{icon}</span>
              <span className="text-[12px] font-semibold">{label}</span>
            </div>
            <span className="text-[10px] px-2 py-0.5 rounded-full border font-bold bg-black/20">
              {index}
            </span>
          </div>
          {sub && <div className="text-[11px] mt-1 text-white/70">{sub}</div>}
        </div>
      </div>
    </button>
  );
}

function InfoCard({ title, value, note }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
      <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
        {title}
      </div>
      <div className="mt-1 text-sm md:text-base font-extrabold text-slate-900">
        {value}
      </div>
      {note && <div className="mt-1 text-[11px] text-slate-600">{note}</div>}
    </div>
  );
}

export default AdminMasterDashboard;
